/**
 * @component
 * @name SignIn
 *
 * @params void
 * @returns JSX.Element
 *
 * @example
 * // Example usage of FormComponent
 * <SignIn />
 *
 * @link https://ocp.earth/auth - FrontendURL
 * @link https://v1-api.ocp.earth/api/auth/signin - BackendURL
 * @link /public/docs/auth/sign-in.png - Screenshot
 *
 * @description Overview:
 * The `SignIn` component is a React component designed to provide a user interface for
 * user authentication through a sign-in form. It utilizes the `react-hook-form` library
 * for form handling and validation and integrates with the `@hookform/resolvers/zod`
 * resolverfor Zod schema validation. This component is part of the authentication flow
 * in a React application.
 *
 * @description Form fields:
 * - Email: Input field for the user's email address. It performs email validation and
 * displays any validation errors.Input field for the user's password. It supports
 * password masking and displays validation errors if any.
 * - Password: Input field for the user's password. It supports password masking and
 * displays validation errors if any.
 *
 * @description Form submission:
 * The component uses the `useForm` hook from `react-hook-form` for managing
 * form state, including form submission, validation, and error handling. It also uses the
 * `useSignIn` custom hook for handling the asynchronous sign-in logic.
 *
 * @description Loader:
 * While the sign-in process is ongoing (loading), the component displays a
 * loading spinner using the `Loader` component.
 *
 * @see https://react-hook-form.com/
 * @see https://zod.dev/
 *
 * @author @Geektrovert <samnan@oneshot.earth>
 * @author @ProdiptaOneShotEarth <prodipta@oneshot.earth>
 *
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import InputField from "@/components/shared/formFields/input";
import Button from "@/components/shared/ui/Button";
import { Loader } from "@/components/shared/ui/Loader";
import useSignIn from "@/hooks/auth/useSignIn";
import type { SignInReqParams } from "@/schemas/auth";
import { signInReqParams } from "@/schemas/auth";

const SignIn = () => {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<SignInReqParams>({
		resolver: zodResolver(signInReqParams),
		mode: "onBlur",
	});

	const { mutate: signIn, isLoading } = useSignIn();

	const onSubmit = (values: SignInReqParams) => {
		let { email, password } = values;
		email = email.toLowerCase();
		signIn({ email, password });
	};

	if (isLoading) {
		return <Loader />;
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<div className="space-y-3">
				<InputField label="Email" type="email" {...register("email")} errorMessage={errors.email?.message} />
				<InputField
					label="Password"
					type="password"
					{...register("password")}
					errorMessage={errors.password?.message}
					showPasswordIcon
				/>
			</div>

			<Button className="mt-6 w-full" type="submit" disabled={isLoading}>
				{isLoading ? "Logging in..." : "Log in to OCP"}
			</Button>
		</form>
	);
};

export default SignIn;
